// src/pages/outsourcing_management/requisitions/RequisitionCreateModal.tsx
//
// EE-207 Phase 3 (#9-#12) - create/edit Draft form. Both entry points - (a)
// Outsourcing > Requisitions > New, and (b) Manufacturing Overview > Material
// > Requisition icon (TaskTableRow "cut" variant) - open this same component
// via the unchanged `open` / `onClose` / `prefill` props. An optional
// `requisition` prop additionally lets this same dialog edit/delete an
// existing Draft (wired for reuse by a future detail view - #13 - without a
// props-contract break today).
//
// Layout: base layer always visible (General Info mandatory, Supplier Info
// optional, Comments) + a Type switch (Raw Material vs Tool) that drives the
// dynamic item-lines editor. Client validation mirrors the backend exactly
// (see requisitionForm.ts) so Save is blocked locally before ever
// hitting the 400 the server would otherwise return.
import {
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { useForm, useStore } from "@tanstack/react-form";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { RequisitionType } from "types/RequisitionTypes";
import { RequisitionItemPrefillRef } from "util/network/Requisitions";
import {
  useCreateRequisition,
  useDeleteDraftRequisition,
  useRequisitionItemPrefills,
  useRequisitionOptions,
  useUpdateRequisition,
} from "hooks/useRequisitions";
import RequisitionGeneralInfoFields from "./RequisitionGeneralInfoFields";
import RequisitionItemLinesEditor from "./RequisitionItemLinesEditor";
import { PickedPart } from "./RequisitionSalesOrderPartPicker";
import {
  PURPOSE_SALES_ORDER,
  PURPOSE_SALES_QUOTE,
  RequisitionFormHeaderValues,
  RequisitionFormItem,
  buildRequisitionPayload,
  emptyHeaderValues,
  hasAnyItemError,
  isMaterialType,
  isPartBasedType,
  itemFromApi,
  itemFromPart,
  itemFromPrefill,
  prefillHasValues,
  validateHeader,
  validateItem,
} from "./requisitionForm";

export type RequisitionCreateModalPrefill = {
  salesOrderId?: number;
  orderItemId?: number;
  // The quote side of the same pair. A requisition is raised from an order or
  // from a quote, never both - the backend refuses both set.
  quoteId?: number;
  quoteItemId?: number;
  // Preselects the type, so an Outsource button that already knows what kind
  // of work this is does not make the user say it again.
  typeCode?: string;
};

type PropsType = {
  open: boolean;
  onClose: () => void;
  prefill?: RequisitionCreateModalPrefill;
  // Optional edit-Draft mode - not used by either current entry point, kept
  // for a future detail view to reuse this same dialog for "update Draft".
  requisition?: RequisitionType;
  onSaved?: (requisition: RequisitionType) => void;
  onDeleted?: () => void;
};

const RequisitionCreateModal: FunctionComponent<PropsType> = ({
  open,
  onClose,
  prefill,
  requisition,
  onSaved,
  onDeleted,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { width: "100%", maxWidth: "min(1400px, 95vw)" } }}
    >
      {open && (
        <RequisitionFormContent
          onClose={onClose}
          prefill={prefill}
          requisition={requisition}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}
    </Dialog>
  );
};

// The part a line was built from, as the picker keyed it. Lines with neither
// were added by hand (Add Line) and belong to no particular part.
const lineMatchesRef = (
  line: { orderItemId?: number; quoteItemId?: number },
  ref: RequisitionItemPrefillRef
) =>
  ref.orderItemId !== undefined
    ? line.orderItemId === ref.orderItemId
    : ref.quoteItemId !== undefined && line.quoteItemId === ref.quoteItemId;

const refKeyOf = (ref: RequisitionItemPrefillRef) =>
  ref.orderItemId !== undefined ? `order:${ref.orderItemId}` : `quote:${ref.quoteItemId}`;

// The parts a saved requisition's lines point at, in line order, without
// repeats - the header's own part is the fallback for lines saved before
// they carried one.
const distinctIds = (
  ids: (number | null | undefined)[],
  fallback: number | null | undefined
): number[] => {
  const seen = Array.from(
    new Set(ids.filter((id): id is number => id !== null && id !== undefined))
  );
  if (seen.length > 0) return seen;
  return fallback !== null && fallback !== undefined ? [fallback] : [];
};

type ContentPropsType = {
  onClose: () => void;
  prefill?: RequisitionCreateModalPrefill;
  requisition?: RequisitionType;
  onSaved?: (requisition: RequisitionType) => void;
  onDeleted?: () => void;
};

const RequisitionFormContent: FunctionComponent<ContentPropsType> = ({
  onClose,
  prefill,
  requisition,
  onSaved,
  onDeleted,
}) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();

  const { data: options } = useRequisitionOptions(token);


  // Captured once when this instance mounts (the dialog only mounts this
  // content while `open`, so a fresh instance/defaultValues snapshot is
  // created every time it's reopened).
  const [initialHeader] = useState<RequisitionFormHeaderValues>(() => {
    if (requisition) {
      return {
        typeCode: requisition.type_code ?? "",
        purposeCode: requisition.purpose_code ?? "",
        salesOrderId: requisition.sales_order_id ?? undefined,
        orderItemIds: distinctIds(
          requisition.items.map((item) => item.order_item_id),
          requisition.order_item_id
        ),
        // Without these a quote-side Draft opened for editing showed the
        // Sales Order picker empty, demanded an order that does not exist,
        // and dropped the quote link on save.
        quoteId: requisition.quote_id ?? undefined,
        quoteItemIds: distinctIds(
          requisition.items.map((item) => item.quote_item_id),
          requisition.quote_item_id
        ),
        supplierId: requisition.supplier_id ?? undefined,
        requiredByDate: requisition.required_by_date?.slice(0, 10) ?? "",
        estimatedDeliveryDate: requisition.estimated_delivery_date?.slice(0, 10) ?? "",
        comments: requisition.comments ?? "",
      };
    }
    return {
      ...emptyHeaderValues(),
      salesOrderId: prefill?.salesOrderId,
      orderItemIds: prefill?.orderItemId ? [prefill.orderItemId] : [],
      quoteId: prefill?.quoteId,
      quoteItemIds: prefill?.quoteItemId ? [prefill.quoteItemId] : [],
      typeCode: prefill?.typeCode ?? "",
    };
  });
  const [items, setItems] = useState<RequisitionFormItem[]>(() =>
    requisition ? requisition.items.map(itemFromApi) : []
  );
  const [showErrors, setShowErrors] = useState(false);

  const form = useForm({
    defaultValues: initialHeader,
    onSubmit: async () => {
      handleSave();
    },
  });
  // Reactive subscription - form.state.values is a point-in-time snapshot and
  // won't trigger re-renders on its own (mirrors RequisitionComponent.tsx).
  const headerValues = useStore(form.store, (s) => s.values);

  const isEdit = !!requisition;

  // Default Type to the first option so the item editor has something to
  // drive off of immediately in create mode.
  useEffect(() => {
    // A prefilled type wins: an Outsource button already knows what kind of
    // work this is.
    if (isEdit || !options || headerValues.typeCode) return;
    const first = options.types[0];
    if (first) form.setFieldValue("typeCode", first.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, isEdit]);

  // A prefilled reference says what the requisition is for, so the Purpose
  // follows it once options load: a Sales Order part gets Sales Order, a
  // quote line gets Sales Quote. Both purposes require a part; which one is
  // decided by where the Outsource button was pressed, not by the picker.
  useEffect(() => {
    if (isEdit || !options || headerValues.purposeCode) return;
    const wanted = prefill?.quoteId
      ? PURPOSE_SALES_QUOTE
      : prefill?.salesOrderId
        ? PURPOSE_SALES_ORDER
        : undefined;
    if (!wanted) return;
    const purpose = options.purposes.find((p) => p.code === wanted);
    if (purpose) form.setFieldValue("purposeCode", purpose.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, isEdit, prefill]);

  // The parts picked on the header, as line refs, in pick order. Read from
  // the form state - present from the first render - and never from the
  // picker: both pickers report an empty list until the order or quote has
  // hydrated, and building lines off that would wipe a Draft's saved lines
  // (or a prefilled part) before the picker had caught up.
  const partRefs = useMemo<RequisitionItemPrefillRef[]>(
    () =>
      headerValues.quoteItemIds.length > 0
        ? headerValues.quoteItemIds.map((quoteItemId) => ({ quoteItemId }))
        : headerValues.orderItemIds.map((orderItemId) => ({ orderItemId })),
    [headerValues.orderItemIds, headerValues.quoteItemIds]
  );
  const partRefsKey = partRefs.map(refKeyOf).join(",");

  // What the picker knows about each picked part once its order or quote has
  // hydrated - the product, name and quantity a part-based line starts from.
  // A lookup only; which parts are picked is the header's to say.
  const [resolvedParts, setResolvedParts] = useState<PickedPart[]>([]);
  const resolvedKey = resolvedParts
    .map((part) => `${refKeyOf(part)}:${part.quantity ?? ""}`)
    .join(",");

  // Everything a material line needs about the part is already known from
  // the product it was quoted from - fetch it (#12) for every picked part
  // and offer each as a starting point rather than making the requester
  // re-key the spec. A quote line is the same part before it was ordered, so
  // it prefills the same way.
  const isMaterial = isMaterialType(headerValues.typeCode);
  const isPartBased = isPartBasedType(headerValues.typeCode);
  const prefills = useRequisitionItemPrefills(token, partRefs, isMaterial);
  const prefillsKey = prefills.map((prefill) => (prefill ? "1" : "0")).join("");

  // Which parts have already had a line built for them. A line is built once
  // per pick: after that the line is the requester's, and it must not be
  // rebuilt under them - a part whose prefill came back empty (an OTHER
  // product) is not marked, so nothing is added for it and Add Line stays
  // the way to describe it. Deselecting a part forgets it, so re-picking
  // builds a fresh line. Editing a Draft starts with every saved part
  // marked: its lines are the saved ones, never a prefill - and a material
  // line saved before lines carried a part would otherwise be doubled by a
  // prefill for the header's part.
  const builtFor = useRef<Set<string>>(
    new Set(requisition ? partRefs.map(refKeyOf) : [])
  );

  // One line per picked part, in pick order, then the lines added by hand.
  // Existing line objects are kept as they are (matched on their part ref),
  // so edits survive a pick or unpick; a line whose part was unpicked goes.
  // Only a changed sequence is written back, so this settles in one pass.
  useEffect(() => {
    if (!isMaterial && !isPartBased) return;
    const keep = new Set(partRefs.map(refKeyOf));
    builtFor.current.forEach((key) => {
      if (!keep.has(key)) builtFor.current.delete(key);
    });

    const next: RequisitionFormItem[] = [];
    partRefs.forEach((ref, index) => {
      const existing = items.find((item) => lineMatchesRef(item, ref));
      if (existing) {
        next.push(existing);
        return;
      }
      const key = refKeyOf(ref);
      if (builtFor.current.has(key)) return;
      let line: RequisitionFormItem | undefined;
      if (isPartBased) {
        const part = resolvedParts.find((candidate) => lineMatchesRef(candidate, ref));
        if (part) line = itemFromPart(part);
      } else {
        const prefill = prefills[index];
        if (prefill && prefillHasValues(prefill)) {
          line = itemFromPrefill(prefill, options?.cuttingAllowanceMm, ref);
        }
      }
      if (line) {
        next.push(line);
        builtFor.current.add(key);
      }
    });
    items.forEach((item) => {
      if (item.orderItemId === undefined && item.quoteItemId === undefined) {
        next.push(item);
      }
    });

    const sameSequence =
      next.length === items.length &&
      next.every((item, index) => item === items[index]);
    if (!sameSequence) setItems(next);
    // Keyed on the strings, not the arrays: the arrays are fresh objects on
    // every render. `items` itself is a dep so a hand-added or edited line
    // is seen; the sameSequence check keeps that from looping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partRefsKey, resolvedKey, prefillsKey, items, isMaterial, isPartBased]);

  const handleTypeChange = (typeCode: string) => {
    if (typeCode !== headerValues.typeCode) {
      setItems([]);
      // The list is empty again, so the picked parts get their lines built
      // afresh for the new type - Raw Material and Processed Raw Material
      // are one click apart and both start from the same parts.
      builtFor.current.clear();
    }
    form.setFieldValue("typeCode", typeCode);
  };

  // The purpose decides what the requisition is raised against, so the
  // reference picked under the previous purpose goes with it - an order
  // line under Sales Quote, or a quote line under Stock, would be saved
  // silently and shown nowhere. The lines go too, for the same reason as on
  // a type change: they were written against that part.
  const handlePurposeChange = (purposeCode: string) => {
    if (purposeCode !== headerValues.purposeCode) {
      form.setFieldValue("salesOrderId", undefined);
      form.setFieldValue("orderItemIds", []);
      form.setFieldValue("quoteId", undefined);
      form.setFieldValue("quoteItemIds", []);
      setResolvedParts([]);
      if (items.length > 0) setItems([]);
      builtFor.current.clear();
    }
    form.setFieldValue("purposeCode", purposeCode);
  };

  const purpose = options?.purposes.find((p) => p.code === headerValues.purposeCode);

  // Removing a line built from a picked part unpicks the part - the header
  // would otherwise build the line again. A hand-added line just goes.
  const handleRemoveLine = (item: RequisitionFormItem) => {
    if (item.orderItemId !== undefined) {
      form.setFieldValue(
        "orderItemIds",
        headerValues.orderItemIds.filter((id) => id !== item.orderItemId)
      );
    } else if (item.quoteItemId !== undefined) {
      form.setFieldValue(
        "quoteItemIds",
        headerValues.quoteItemIds.filter((id) => id !== item.quoteItemId)
      );
    } else {
      setItems(items.filter((candidate) => candidate._key !== item._key));
    }
  };

  const headerErrors = useMemo(
    () => validateHeader(headerValues, options),
    [headerValues, options]
  );
  const itemErrorsList = useMemo(
    () => items.map((item) => validateItem(headerValues.typeCode, item, options)),
    [items, headerValues.typeCode, options]
  );
  const hasErrors =
    Object.keys(headerErrors).length > 0 ||
    items.length === 0 ||
    itemErrorsList.some(hasAnyItemError);

  const invalidateAndClose = (savedRequisition: RequisitionType) => {
    onSaved?.(savedRequisition);
    onClose();
  };

  const createMutation = useCreateRequisition(token, {
    onSuccess: (data) => {
      addToast({
        type: "success",
        message: `Requisition ${data.requisition_no ?? `#${data.id}`} saved as Draft`,
      });
      invalidateAndClose(data);
    },
    onError: (err) => {
      addToast({
        type: "error",
        message: `Could not create the requisition: ${err}`,
        keep: true,
      });
    },
  });

  const updateMutation = useUpdateRequisition(token, {
    onSuccess: (data) => {
      addToast({
        type: "success",
        message: `Requisition ${data.requisition_no ?? `#${data.id}`} updated`,
      });
      invalidateAndClose(data);
    },
    onError: (err) => {
      addToast({
        type: "error",
        message: `Could not update the requisition: ${err}`,
        keep: true,
      });
    },
  });

  const deleteMutation = useDeleteDraftRequisition(token, {
    onSuccess: () => {
      addToast({ type: "success", message: "Draft requisition deleted" });
      onDeleted?.();
      onClose();
    },
    onError: (err) => {
      addToast({
        type: "error",
        message: `Could not delete the draft: ${err}`,
        keep: true,
      });
    },
  });

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  const handleSave = () => {
    setShowErrors(true);
    if (hasErrors) {
      addToast({
        type: "error",
        message: "Please fix the highlighted fields before saving.",
      });
      return;
    }
    const payload = buildRequisitionPayload(headerValues, items, options);
    if (requisition) {
      updateMutation.mutate({ id: requisition.id, requisition: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = () => {
    if (!requisition) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this Draft requisition? This cannot be undone.")) return;
    // Pass the linked part so the SO Part List's red indicator (#18) clears
    // immediately - DELETE returns a bare boolean, so the hook can't discover
    // the order item on its own.
    deleteMutation.mutate({
      id: requisition.id,
      orderItemIds: [
        requisition.order_item_id,
        ...requisition.items.map((item) => item.order_item_id),
      ],
    });
  };

  return (
    <>
      <DialogTitle>
        {isEdit
          ? `Edit Draft Requisition ${requisition?.requisition_no ?? `#${requisition?.id}`}`
          : "New Requisition"}
      </DialogTitle>
      <DialogContent dividers>
        {(prefill?.salesOrderId || prefill?.orderItemId) && !isEdit && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Prefilled from Sales Order #{prefill.salesOrderId}
            {prefill.orderItemId && ` / Part #${prefill.orderItemId}`}
          </Typography>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <RequisitionGeneralInfoFields
            form={form}
            values={headerValues}
            options={options}
            token={token}
            headerErrors={headerErrors}
            showErrors={showErrors}
            onTypeChange={handleTypeChange}
            onPurposeChange={handlePurposeChange}
            onPartsResolved={setResolvedParts}
          />

          <Divider sx={{ my: 2 }} />

          <RequisitionItemLinesEditor
            typeCode={headerValues.typeCode}
            items={items}
            onChange={setItems}
            options={options}
            showErrors={showErrors}
            // Part-based lines under a part-requiring purpose come from the
            // header's parts, one each: a blank line would have no part.
            allowAddLine={!isPartBased || !purpose?.requiresSalesOrderAndPart}
            onRemoveLine={handleRemoveLine}
          />
        </form>
      </DialogContent>
      <DialogActions>
        {isEdit && (
          <Button
            color="error"
            onClick={handleDelete}
            disabled={deleteMutation.isLoading || isSaving}
            sx={{ marginRight: "auto" }}
          >
            {deleteMutation.isLoading ? <CircularProgress size={18} /> : "Delete Draft"}
          </Button>
        )}
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          sx={{
            background: "var(--mainYellow)",
            "&:hover": {
              color: "var(--mainYellow)",
              border: "1px solid var(--mainYellow)",
              background: "transparent",
            },
          }}
        >
          {isSaving ? (
            <CircularProgress size={18} />
          ) : isEdit ? (
            "Update Draft"
          ) : (
            "Save as Draft"
          )}
        </Button>
      </DialogActions>
    </>
  );
};

export default RequisitionCreateModal;
